import { getIntentDisplayLabel } from "./config/constants";
import {
  normalizeToRequestLogStatus,
  resolveValuePresentation,
} from "./config/filter-value-type-behavior";
import {
  FilterValueType,
  ValueDisplayType,
  type FilterDefinition,
} from "./config/structured-search-schema";
import { formatFilterValueByType } from "./config/value-formatter";

import { ProviderWithLogo } from "@/components/ProviderWithLogo";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { RequestStatusBadge } from "@/components/ui/RequestStatusBadge";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";

interface FilterValueRendererRouterProps {
  definition: FilterDefinition | undefined;
  value: string;
  label?: string;
  className?: string;
  badgeClassName?: string;
  textClassName?: string;
  size?: BadgeSize;
  onRemove?: () => void;
}

const renderDefaultValue = ({
  label,
  value,
  badgeClassName,
  textClassName,
  size,
  onRemove,
}: Pick<
  FilterValueRendererRouterProps,
  "label" | "value" | "badgeClassName" | "textClassName" | "size" | "onRemove"
>) => {
  const displayLabel = label ?? value;

  if (onRemove) {
    return (
      <Badge
        variant={BadgeVariant.BLUE}
        size={size ?? BadgeSize.SM}
        className={cn("min-w-0 shrink", badgeClassName)}
        onRemove={onRemove}
      >
        <OverflowDisplayTooltip content={displayLabel}>
          <span className={textClassName ?? "max-w-[160px] truncate"}>{displayLabel}</span>
        </OverflowDisplayTooltip>
      </Badge>
    );
  }

  return <span className={cn("truncate", textClassName)}>{displayLabel}</span>;
};

const renderEnumValue = ({
  definition,
  value,
  label,
  className,
  badgeClassName,
  textClassName,
  size,
  onRemove,
}: FilterValueRendererRouterProps & { definition: FilterDefinition }) => {
  const presentation = resolveValuePresentation(definition);

  switch (presentation) {
    case ValueDisplayType.RequestStatus:
    case ValueDisplayType.TraceStatus: {
      const status = normalizeToRequestLogStatus(value);
      if (!status) {
        return renderDefaultValue({
          value,
          label,
          badgeClassName,
          textClassName,
          size,
          onRemove,
        });
      }

      return (
        <RequestStatusBadge
          status={status}
          size={size ?? BadgeSize.SM}
          className={className ?? badgeClassName}
          onRemove={onRemove}
        />
      );
    }

    case ValueDisplayType.Provider:
      return <ProviderWithLogo provider={value} size="sm" />;

    case ValueDisplayType.Intent:
      return renderDefaultValue({
        value,
        label: getIntentDisplayLabel(value),
        badgeClassName,
        textClassName,
        size,
        onRemove,
      });

    default: {
      const enumLabel =
        label ?? definition.options?.find(option => option.value === value)?.label ?? value;
      return renderDefaultValue({
        value,
        label: enumLabel,
        badgeClassName,
        textClassName,
        size,
        onRemove,
      });
    }
  }
};

const FilterValueRendererRouter = ({
  definition,
  value,
  label,
  className,
  badgeClassName,
  textClassName,
  size = BadgeSize.SM,
  onRemove,
}: FilterValueRendererRouterProps) => {
  if (!definition) {
    return renderDefaultValue({ value, label, badgeClassName, textClassName, size, onRemove });
  }

  switch (definition.type) {
    case FilterValueType.Enum:
      return renderEnumValue({
        definition,
        value,
        label,
        className,
        badgeClassName,
        textClassName,
        size,
        onRemove,
      });

    case FilterValueType.Cost:
      return renderDefaultValue({
        value,
        label:
          label ??
          formatFilterValueByType({
            type: FilterValueType.Cost,
            value: Number(value),
            operator: "eq",
          }),
        badgeClassName,
        textClassName,
        size,
        onRemove,
      });

    case FilterValueType.Latency:
      return renderDefaultValue({
        value,
        label: label ?? value,
        badgeClassName,
        textClassName,
        size,
        onRemove,
      });

    case FilterValueType.Boolean:
      return null;

    case FilterValueType.Prompt:
      return renderDefaultValue({
        value,
        label: label ?? value,
        badgeClassName,
        textClassName,
        size,
        onRemove,
      });

    case FilterValueType.Nested:
    case FilterValueType.String:
    case FilterValueType.Number:
    case FilterValueType.Date:
    case FilterValueType.DateRange:
    default:
      return renderDefaultValue({
        value,
        label,
        badgeClassName,
        textClassName,
        size,
        onRemove,
      });
  }
};

export default FilterValueRendererRouter;
